import React, { memo, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Upload, XCircle, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { clsx } from 'clsx';
import { Button } from '@/components/ui/button';
import ErrorBoundary from '@/components/ErrorBoundary';

const FileUploadSectionComponent = ({
  file,
  errorMessage,
  handleFileChange,
  uploadProgress,
  isUploading,
  uploadStats,
  abortUpload,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Add explanatory header
  const renderHeader = () => (
    <div className="space-y-2 text-center mb-6">
      <h2 className="text-2xl font-semibold tracking-tight">Bulk Property Upload</h2>
      <p className="text-muted-foreground text-sm">
        Upload a CSV or XLSX with property addresses to retrieve real estate details. You'll see a
        preview and pricing before purchasing.
      </p>
    </div>
  );

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      setIsDragActive(false);

      // Clear previous errors on new drop attempt
      handleFileChange(null);

      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const errorCode = rejection.errors[0].code;

        let errorMessage = '';
        switch (errorCode) {
          case 'file-invalid-type':
            errorMessage = 'Invalid file type - Only CSV, XLSX, or XLS files are accepted';
            break;
          case 'file-too-large':
            errorMessage = 'File too large - Maximum size 200MB';
            break;
          default:
            errorMessage = 'Invalid file - Please check format and try again';
        }

        handleFileChange({ error: errorMessage });
        return;
      }

      // Additional validation for file extension
      const file = acceptedFiles[0];
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const isValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

      if (!isValidExtension) {
        handleFileChange({
          error: `Invalid file type (${file.name.split('.').pop().toUpperCase()}) - Only CSV/XLSX files allowed`,
        });
        return;
      }

      handleFileChange(file);
    },
    [handleFileChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxSize: 200 * 1024 * 1024, // 200MB max size
    noClick: true,
  });

  return (
    <ErrorBoundary fallback={<div className="text-red-500">Upload component failed</div>}>
      <div className="w-full max-w-4xl space-y-6 py-4">
        {renderHeader()}
        <div
          {...getRootProps()}
          className={clsx(
            'file-upload-box border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200',
            errorMessage
              ? 'border-red-500 bg-red-50/10' // Error state
              : file
                ? 'border-green-500 bg-green-50/10'
                : isDragActive
                  ? 'border-primary bg-surface/20'
                  : 'border-border-color hover:border-secondary/70'
          )}
          tabIndex={0}
        >
          <div className="flex justify-center mb-6">
            {errorMessage ? (
              <XCircle className="h-12 w-12 text-red-500 animate-pulse" />
            ) : file ? (
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            ) : (
              <Upload
                className={clsx(
                  'h-12 w-12 text-secondary transition-transform duration-200',
                  isDragActive && 'scale-110 text-primary'
                )}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="file-upload" className="cursor-pointer text-base block">
              {errorMessage ? (
                <span className="text-red-600 font-medium">Invalid File - </span>
              ) : (
                <span className="text-primary font-medium">Upload Spreadsheet </span>
              )}
              <span className="text-secondary/70">
                {errorMessage ? 'Try Again' : 'or Drag File'}
              </span>
              <input
                {...getInputProps()}
                id="file-upload"
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={(e) => {
                  // Clear errors before validation
                  handleFileChange(null);

                  const files = e?.target?.files;
                  const selectedFile = files?.[0];

                  if (!selectedFile) {
                    return;
                  }

                  // Validate file type for direct input selection
                  const validExtensions = ['.csv', '.xlsx', '.xls'];
                  const isValidExtension = validExtensions.some((ext) =>
                    selectedFile.name.toLowerCase().endsWith(ext)
                  );

                  if (!isValidExtension) {
                    handleFileChange({
                      error: `Invalid file type (${selectedFile.name.split('.').pop().toUpperCase()}) - Only CSV/XLSX/XLS allowed`,
                    });
                    e.target.value = '';
                    return;
                  }

                  // Additional size validation
                  if (selectedFile.size > 200 * 1024 * 1024) {
                    handleFileChange({
                      error: 'File too large - Maximum size 200MB',
                    });
                    e.target.value = '';
                    return;
                  }

                  handleFileChange(selectedFile);
                  e.target.value = '';
                }}
                accept=".csv,.xlsx,.xls"
              />
            </Label>
            <p className="text-secondary/70 text-sm mt-4">
              Supported formats: CSV or XLSX (Max 200 MB)
            </p>
            {errorMessage && (
              <div className="text-red-500 text-sm mt-2 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                <span>{errorMessage}</span>
              </div>
            )}
            {file && (
              <div className="mt-4 flex flex-col items-center gap-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-green-500 font-medium">{file.name}</span>
                </div>
                <span className="text-xs text-green-500/80">
                  {file.type} - {Math.round(file.size / 1024)}KB
                </span>
                {isUploading && (
                  <div className="w-full max-w-xs mt-4">
                    <div className="space-y-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {Math.round(uploadProgress)}% Complete
                        </span>
                        {uploadProgress > 0 && uploadProgress < 100 && (
                          <Button
                            variant="destructive"
                            onClick={() => {
                              abortUpload();
                              handleFileChange(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                            disabled={!isUploading}
                          >
                            Cancel Upload
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-primary/80">
                      <span>Upload Progress: {uploadProgress}%</span>
                      {uploadStats.speed && (
                        <span>
                          {uploadStats.speed} • {uploadStats.eta} remaining
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

const MemoizedFileUploadSection = memo(FileUploadSectionComponent);
MemoizedFileUploadSection.displayName = 'FileUploadSection';
MemoizedFileUploadSection.propTypes = {
  file: PropTypes.object,
  errorMessage: PropTypes.string,
  handleFileChange: PropTypes.func.isRequired,
  uploadProgress: PropTypes.number,
  isUploading: PropTypes.bool,
  uploadStats: PropTypes.object,
  abortUpload: PropTypes.func.isRequired,
};

export default MemoizedFileUploadSection;
